/**
 * PDF Export Utility
 *
 * Generates PDF reports from decrypted event data using jsPDF.
 * All processing happens client-side (zero-knowledge).
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, differenceInMonths, differenceInDays } from 'date-fns';
import { EventType } from '../types';
import type { DecryptedEvent } from './exportCSV';

export interface GrowthDataPoint {
  date: Date;
  weight?: number;
  weightUnit?: string;
  height?: number;
  temperature?: number;
  tempUnit?: string;
}

export interface PDFExportOptions {
  babyName: string;
  dateOfBirth?: Date;
  events: DecryptedEvent[];
  growthData?: GrowthDataPoint[];
  dateFrom?: Date;
  dateTo?: Date;
  eventTypes?: EventType[];
}

/**
 * Get event type display info
 */
const EVENT_DISPLAY: Record<string, { label: string; color: [number, number, number] }> = {
  FEED: { label: 'Feed', color: [59, 130, 246] },
  NAPPY: { label: 'Nappy', color: [234, 179, 8] },
  SLEEP: { label: 'Sleep', color: [139, 92, 246] },
  SPIT_UP: { label: 'Spit Up', color: [249, 115, 22] },
  MEDICATION: { label: 'Medication', color: [239, 68, 68] },
  WEIGHT: { label: 'Weight', color: [34, 197, 94] },
  TEMPERATURE: { label: 'Temperature', color: [236, 72, 153] },
  TUMMY_TIME: { label: 'Tummy Time', color: [20, 184, 166] },
  PUMP: { label: 'Pump', color: [99, 102, 241] },
  BOTTLE: { label: 'Bottle', color: [6, 182, 212] },
  JOURNAL: { label: 'Journal', color: [107, 114, 128] },
};

/**
 * Format event data for PDF display
 */
function formatEventDetails(type: EventType, data: Record<string, unknown>): string {
  const parts: string[] = [];

  switch (type) {
    case EventType.FEED:
      if (data.feedType) parts.push(String(data.feedType));
      if (data.side) parts.push(`${data.side} side`);
      if (data.duration) parts.push(`${data.duration}min`);
      if (data.amount) parts.push(`${data.amount}ml`);
      break;

    case EventType.NAPPY:
      if (data.wet) parts.push('Wet');
      if (data.dirty) parts.push('Dirty');
      if (data.rash) parts.push('Rash');
      break;

    case EventType.SLEEP:
      if (data.sleepType) parts.push(String(data.sleepType));
      if (data.duration) parts.push(`${data.duration}min`);
      break;

    case EventType.MEDICATION:
      if (data.medicationName) parts.push(String(data.medicationName));
      if (data.dose) parts.push(String(data.dose));
      break;

    case EventType.WEIGHT:
      if (data.weight) parts.push(`${data.weight}${data.unit || 'kg'}`);
      if (data.height) parts.push(`${data.height}cm`);
      break;

    case EventType.TEMPERATURE:
      if (data.temperature) parts.push(`${data.temperature}°${data.tempUnit || 'C'}`);
      if (data.method) parts.push(String(data.method));
      break;

    case EventType.TUMMY_TIME:
      if (data.duration) parts.push(`${data.duration}min`);
      if (data.mood) parts.push(String(data.mood));
      break;

    case EventType.PUMP:
      const total =
        (Number(data.leftAmount) || 0) + (Number(data.rightAmount) || 0);
      if (total > 0) parts.push(`${total}ml total`);
      break;

    case EventType.BOTTLE:
      if (data.bottleType) parts.push(String(data.bottleType));
      if (data.amountOffered) parts.push(`${data.amountOffered}ml`);
      break;

    case EventType.SPIT_UP:
      if (data.severity) parts.push(String(data.severity));
      break;

    case EventType.JOURNAL:
      if (data.title) parts.push(String(data.title));
      if (data.isMilestone) parts.push('(Milestone)');
      break;
  }

  return parts.join(', ') || '-';
}

/**
 * Calculate age string from date of birth
 */
function calculateAge(dateOfBirth?: Date): string {
  if (!dateOfBirth) return 'N/A';
  const now = new Date();
  const months = differenceInMonths(now, dateOfBirth);
  const days = differenceInDays(now, dateOfBirth) % 30;

  if (months < 1) {
    return `${differenceInDays(now, dateOfBirth)} days`;
  } else if (months < 12) {
    return `${months} month${months !== 1 ? 's' : ''}, ${days} day${days !== 1 ? 's' : ''}`;
  } else {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
  }
}

/**
 * Generate a PDF report
 */
export async function generatePDF(options: PDFExportOptions): Promise<Blob> {
  const { babyName, dateOfBirth, events, dateFrom, dateTo } = options;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // ===== HEADER =====
  doc.setFontSize(24);
  doc.setTextColor(79, 70, 229); // Primary color
  doc.text(`${babyName}'s Health Report`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 12;

  // Subtitle with date range
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128); // Gray
  const dateRangeText = dateFrom && dateTo
    ? `${format(dateFrom, 'MMM d, yyyy')} - ${format(dateTo, 'MMM d, yyyy')}`
    : 'All Time';
  doc.text(dateRangeText, pageWidth / 2, yPos, { align: 'center' });
  yPos += 20;

  // ===== BABY INFO BOX =====
  doc.setDrawColor(229, 231, 235);
  doc.setFillColor(249, 250, 251);
  doc.roundedRect(14, yPos, pageWidth - 28, 30, 3, 3, 'FD');

  doc.setFontSize(11);
  doc.setTextColor(31, 41, 55);
  doc.text('Baby Information', 20, yPos + 8);

  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text(`Date of Birth: ${dateOfBirth ? format(dateOfBirth, 'MMMM d, yyyy') : 'N/A'}`, 20, yPos + 16);
  doc.text(`Current Age: ${calculateAge(dateOfBirth)}`, 20, yPos + 22);
  doc.text(`Report Generated: ${format(new Date(), 'MMMM d, yyyy h:mm a')}`, pageWidth / 2 + 10, yPos + 16);
  doc.text(`Total Events: ${events.length}`, pageWidth / 2 + 10, yPos + 22);

  yPos += 40;

  // ===== SUMMARY SECTION =====
  doc.setFontSize(14);
  doc.setTextColor(31, 41, 55);
  doc.text('Activity Summary', 14, yPos);
  yPos += 8;

  // Count events by type
  const eventCounts: Record<string, number> = {};
  for (const event of events) {
    eventCounts[event.type] = (eventCounts[event.type] || 0) + 1;
  }

  // Create summary grid
  const summaryData: string[][] = [];
  const types = Object.keys(eventCounts).sort(
    (a, b) => eventCounts[b] - eventCounts[a]
  );

  for (let i = 0; i < types.length; i += 3) {
    const row: string[] = [];
    for (let j = 0; j < 3; j++) {
      const type = types[i + j];
      if (type) {
        const display = EVENT_DISPLAY[type] || { label: type, color: [107, 114, 128] };
        row.push(`${display.label}: ${eventCounts[type]}`);
      } else {
        row.push('');
      }
    }
    summaryData.push(row);
  }

  if (summaryData.length > 0) {
    autoTable(doc, {
      startY: yPos,
      body: summaryData,
      theme: 'plain',
      styles: {
        fontSize: 9,
        cellPadding: 4,
      },
      columnStyles: {
        0: { cellWidth: (pageWidth - 28) / 3 },
        1: { cellWidth: (pageWidth - 28) / 3 },
        2: { cellWidth: (pageWidth - 28) / 3 },
      },
      margin: { left: 14, right: 14 },
    });
    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // ===== EVENTS TABLE =====
  doc.setFontSize(14);
  doc.setTextColor(31, 41, 55);
  doc.text('Activity Log', 14, yPos);
  yPos += 8;

  // Sort events by timestamp (newest first)
  const sortedEvents = [...events].sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
  );

  // Prepare table data
  const tableData = sortedEvents.map((event) => {
    const display = EVENT_DISPLAY[event.type] || { label: event.type, color: [107, 114, 128] };
    return [
      format(event.timestamp, 'MMM d'),
      format(event.timestamp, 'h:mm a'),
      display.label,
      formatEventDetails(event.type, event.data),
    ];
  });

  autoTable(doc, {
    startY: yPos,
    head: [['Date', 'Time', 'Type', 'Details']],
    body: tableData,
    headStyles: {
      fillColor: [79, 70, 229],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    columnStyles: {
      0: { cellWidth: 25 },
      1: { cellWidth: 25 },
      2: { cellWidth: 30 },
      3: { cellWidth: 'auto' },
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      // Footer on each page
      const pageCount = doc.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
      doc.text(
        'Generated by myTotTot - Privacy-first baby tracking',
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 5,
        { align: 'center' }
      );
    },
  });

  // Return as blob
  return doc.output('blob');
}

/**
 * Trigger a PDF file download
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Export events to PDF and trigger download
 */
export async function exportToPDF(options: PDFExportOptions): Promise<void> {
  const blob = await generatePDF(options);
  const filename = `${options.babyName.replace(/\s+/g, '_')}_report_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  downloadBlob(blob, filename);
}
